import {
  type ApiRoleStrategy,
  ROLES,
  api,
  stringifyQueryObj,
  useQuery,
  useRoleConf,
} from "@pharmupp/p3-front-commons"
import { useParams } from "react-router-dom"
import { type ApiOrder, useOffersOrdersReferentialApi } from "../AdherentOrdersList"
import { getOrdersSelector } from "../AdherentOrdersList/tabs/commons"
import { useOrderPageApi } from "./useOrderPageApi"

export const useAdherentSdavChallengeItemOrdersApi = ({
  itemId,
  year,
}: {
  itemId: number
  year: number
}) => {
  const { adherentId } = useParams()
  const { orderPage, isLoading: isOrderPageLoading } = useOrderPageApi({
    itemId,
    year,
    adherentId,
  })

  // Order referential
  const { referential, isLoading: isReferentialLoading } =
    useOffersOrdersReferentialApi()

  // Orders list
  const {
    endpoint: orderListEndpoint,
    queryKey: orderListQueryKey,
    queryParams: orderListQueryParams,
  } = useRoleConf(orderListApiRoleConf(adherentId))

  const queryParams = stringifyQueryObj({
    ...orderListQueryParams,
    year,
    startIndex: orderPage?.startIndex,
    endIndex: orderPage?.endIndex,
  })
  const {
    data: orders,
    isLoading: isOrdersLoading,
    isPlaceholderData: contentLoading,
    refetch: refresh,
  } = useQuery({
    queryKey: [
      ...orderListQueryKey,
      orderPage?.startIndex,
      orderPage?.endIndex,
      year,
    ],
    queryFn: () => api.get<ApiOrder[]>(`${orderListEndpoint}?${queryParams}`),
    enabled: !!orderPage,
    select: getOrdersSelector({ referential }),
  })

  return {
    orders,
    isLoading: isOrderPageLoading || isOrdersLoading || isReferentialLoading,
    isContentLoading: contentLoading,
    refresh,
  }
}

const presidentApiRoleConf = (adherentId: string) => ({
  endpoint: "/api/offers/president/orders/challenge",
  queryKey: ["offers", "president", "orders", "challenge", adherentId],
  queryParams: { adherentId },
})

const orderListApiRoleConf = (
  adherentId?: string,
): ApiRoleStrategy<{ queryParams: Record<string, unknown> }> => ({
  [ROLES.ADHERENT_HOLDER]: {
    endpoint: "/api/offers/my-orders/sdav-challenge",
    queryKey: ["offers", "my-orders", "sdav-challenge"],
    queryParams: {},
  },
  ...(adherentId && {
    [ROLES.UPP_PRESIDENT]: presidentApiRoleConf(adherentId),
    [ROLES.UPP_REPRESENTATIVE]: presidentApiRoleConf(adherentId),
    [ROLES.ADMIN]: {
      endpoint: "/api/offers/orders/challenge",
      queryKey: ["offers", "orders", "challenge", adherentId],
      queryParams: { adherentId },
    },
    [ROLES.ADHERENT_ADVISOR]: {
      endpoint: "/api/offers/my-sector/orders/challenge",
      queryKey: ["offers", "my-sector", "orders", "challenge", adherentId],
      queryParams: { adherentId },
    },
  }),
})
